import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const OTP_LEN = 6;

export default function ResetPassword() {
    const { refresh } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [params] = useSearchParams();
    const passedEmail = loc.state?.email || params.get("email") || "";

    const [email, setEmail] = useState(passedEmail);
    const [digits, setDigits] = useState(Array(OTP_LEN).fill(""));
    const [newPwd, setNewPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(60);
    const [err, setErr] = useState("");
    const [info, setInfo] = useState("");
    const inputs = useRef([]);

    useEffect(() => { inputs.current[0]?.focus(); }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const onDigit = (i, v) => {
        const ch = v.replace(/\D/g, "").slice(-1);
        const next = [...digits];
        next[i] = ch;
        setDigits(next);
        if (ch && i < OTP_LEN - 1) inputs.current[i + 1]?.focus();
    };
    const onKey = (i, e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
    };
    const onPaste = (e) => {
        const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
        if (!text) return;
        e.preventDefault();
        const next = Array(OTP_LEN).fill("");
        for (let i = 0; i < text.length; i++) next[i] = text[i];
        setDigits(next);
        inputs.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
    };

    const submit = async (e) => {
        e.preventDefault();
        const otp = digits.join("");
        if (!email) return setErr("Please enter your email.");
        if (otp.length !== OTP_LEN) return setErr("Please enter the 6-digit verification code.");
        if (newPwd.length < 6) return setErr("Password must be at least 6 characters.");
        setErr(""); setInfo("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/reset-password", { email, otp, new_password: newPwd });
            if (data.token) {
                localStorage.setItem("gpm_token", data.token);
                await refresh();
            }
            nav("/dashboard");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
            setDigits(Array(OTP_LEN).fill(""));
            inputs.current[0]?.focus();
        } finally {
            setSubmitting(false);
        }
    };

    const onResend = async () => {
        if (!email) return setErr("Please enter your email.");
        setErr(""); setInfo("");
        setResending(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setInfo("A new code has been emailed.");
            setCooldown(60);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-navy-900 grid-pattern px-6 py-12">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-28 h-28 flex items-center justify-center">
                        <img src="/gpm-logo.png" alt="GPM" className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.5)]" />
                    </div>
                    <div className="text-left">
                        <div className="font-heading font-bold text-white text-xl leading-tight">Global Property Mart</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold-500/90">One Platform · Every Need</div>
                    </div>
                </Link>

                <form onSubmit={submit} className="rounded-3xl glass p-8 gold-border-glow" data-testid="reset-password-card">
                    <div className="flex items-center gap-2 text-gold-500">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="text-[11px] tracking-widest uppercase font-bold">Reset Password</span>
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-white mt-2">Set a new password</h1>
                    <p className="text-white/65 text-sm mt-1">
                        Enter the 6-digit code from your email and choose a new password (min 6 characters).
                    </p>

                    {!passedEmail && (
                        <div className="mt-5">
                            <label className="text-xs text-white/60 mb-1 block flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> Email
                            </label>
                            <input
                                type="email"
                                data-testid="reset-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white"
                                placeholder="you@example.com"
                            />
                        </div>
                    )}

                    <div className="mt-5">
                        <label className="text-xs text-white/60 mb-2 block">Verification code</label>
                        <div className="flex gap-2 sm:gap-3 justify-center" onPaste={onPaste} data-testid="reset-otp-digits">
                            {digits.map((d, i) => (
                                <input
                                    key={i}
                                    ref={(el) => (inputs.current[i] = el)}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    value={d}
                                    onChange={(e) => onDigit(i, e.target.value)}
                                    onKeyDown={(e) => onKey(i, e)}
                                    data-testid={`reset-otp-digit-${i}`}
                                    className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold bg-navy-900 border-2 border-white/10 rounded-xl text-white focus:border-gold-500 focus:outline-none transition-colors"
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-5">
                        <label className="text-xs text-white/60 mb-1 block">New password</label>
                        <div className="relative">
                            <input
                                type={showPwd ? "text" : "password"}
                                data-testid="reset-new-password"
                                value={newPwd}
                                onChange={(e) => setNewPwd(e.target.value)}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 pr-10 text-sm text-white"
                                placeholder="At least 6 characters"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd(!showPwd)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                                tabIndex={-1}
                                data-testid="reset-toggle-show-pwd"
                            >
                                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {err && <div className="mt-4 text-sm text-red-400 text-center" data-testid="reset-password-error">{err}</div>}
                    {info && !err && <div className="mt-4 text-sm text-emerald-400 text-center">{info}</div>}

                    <button
                        type="submit"
                        disabled={submitting || digits.some((d) => !d) || newPwd.length < 6}
                        data-testid="reset-password-submit"
                        className="w-full mt-6 px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-50"
                    >
                        {submitting ? "Resetting…" : "Reset password & sign in"}
                    </button>

                    <div className="mt-5 text-center text-sm text-white/65">
                        Didn't get the code?{" "}
                        {cooldown > 0 ? (
                            <span className="text-white/45">Resend in {cooldown}s</span>
                        ) : (
                            <button
                                type="button"
                                onClick={onResend}
                                disabled={resending}
                                data-testid="reset-resend-btn"
                                className="text-gold-500 font-medium hover:text-gold-100 underline-offset-2 hover:underline disabled:opacity-50"
                            >
                                {resending ? "Sending…" : "Resend code"}
                            </button>
                        )}
                    </div>

                    <div className="mt-3 text-center text-xs text-white/45">
                        Remembered it? <Link to="/login" className="text-gold-500/80 hover:text-gold-500">Back to login</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
